import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ProgressBar,
  SearchBarProps,
  styled,
  Text,
} from '@zeiss/beyond-online-react';
import { Box, EmptyState } from '@zeiss/lib-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InvitationsTable } from './';
import { ActionBar } from 'src/components/shared/ActionBar';
import { CreateButton } from 'src/components/shared/CreateButton';
import { useInvitations } from 'src/hooks/api';
import {
  SendInvitationModal,
  SendInvitationModalData,
} from 'src/modules/Invitations/components/SendInvitationModal';

const Container = styled.div`
  display: flex;
  justify-content: center;
`;

export const Result = () => {
  const { t } = useTranslation(['common', 'invitations']);
  const queryClient = useQueryClient();

  const [selectedInvitation, setSelectedInvitation] =
    useState<SendInvitationModalData | null>(null);
  const [searchParam, setSearchParam] = useState<string>('');

  const {
    data: invitationsData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInvitations({ searchQuery: searchParam });

  const invitations =
    invitationsData?.pages.flatMap((page) => page.invitations) ?? [];

  const handleSearch: SearchBarProps['onSearch'] = (_, { searchValue }) => {
    setSearchParam(searchValue);
  };

  const handleOpenSendInvitationModal = (payload: SendInvitationModalData) => {
    setSelectedInvitation(payload);
  };

  const handleCloseSendInvitationModal = () => {
    setSelectedInvitation(null);
  };

  const handleSendInvitationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['invitations', searchParam],
    });
  };

  const renderResult = () => {
    if (isPending) {
      return <ProgressBar animate="spinning" data-testid="LoadingSpinner" />;
    }

    if (error) {
      return <div>Error loading data</div>;
    }

    if (invitations.length > 0) {
      return (
        <>
          <Box>
            <InvitationsTable
              data={invitations}
              onSendInvitation={handleOpenSendInvitationModal}
            />
          </Box>
          <Box>
            <Container>
              {hasNextPage ? (
                <Button
                  disabled={isFetchingNextPage}
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  variant="secondary"
                >
                  {t('invitations:action.loadMore')}
                </Button>
              ) : (
                <Text>
                  {searchParam
                    ? t('invitations:allMatchingResultsLoaded')
                    : t('invitations:allEntriesLoaded')}
                </Text>
              )}
            </Container>
          </Box>
        </>
      );
    }

    if (invitations.length === 0 && !searchParam) {
      return (
        <EmptyState
          description={t('common:emptyState.invitations')}
          headline={t('common:emptyState.textNoX', {
            x: t('common:glossary.invitation_other'),
          })}
          illustrationConfig={{
            img: 'ClearedData',
          }}
        />
      );
    }

    return (
      <EmptyState
        description={t('common:emptyState.tryDifferentTerm')}
        headline={t('common:emptyState.noXfound', {
          x: t('common:glossary.invitation'),
        })}
        illustrationConfig={{
          img: 'SearchNotFound',
        }}
      />
    );
  };

  return (
    <>
      <ActionBar
        onSearch={handleSearch}
        searchableFields={`${t('common:glossary.inviteCode')}, ${t('common:glossary.email')}, ${t('common:applicationName')}, ${t('common:tenantName')}`}
      >
        <CreateButton transX={t('common:glossary.invitation')} />
      </ActionBar>

      {selectedInvitation ? (
        <SendInvitationModal
          data={selectedInvitation}
          isOpen
          onClose={handleCloseSendInvitationModal}
          onSuccess={handleSendInvitationSuccess}
        />
      ) : null}

      {renderResult()}
    </>
  );
};
